import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BarChart2, Users } from 'lucide-react';
import { API_URL } from '../config';
import { formatCurrency } from '../utils/formatters';

// Format timestamp to IST timezone
const formatToIST = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }) + ' IST';
};

// Modal Component
const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
  if (!isOpen) return null;
  
  const sizeClasses = {
    md: 'max-w-md',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`bg-[#09090B] rounded-2xl border border-white/10 shadow-2xl w-full ${sizeClasses[size]} relative z-10 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#09090B] z-10">
          <h3 className="font-bold text-lg text-white">{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-5">
          {children}
        </div>
      </div>
    </div>
  );
};

// Input Component
const Input = ({ label, type = 'text', value, onChange, placeholder, required = false }) => (
  <div className="space-y-2">
    <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="w-full px-4 py-3 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-500 outline-none focus:border-[#6366f1] focus:ring-2 focus:ring-[#6366f1]/20 transition-colors"
    />
  </div>
);

// Account Access Modal Component
const AccountAccessModal = ({ isOpen, onClose, client, addToast, onUpdate }) => {
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');

  // Fetch all accounts and client's current access
  useEffect(() => {
    if (isOpen && client) {
      const fetchData = async () => {
        setLoading(true);
        try {
          // Fetch all accounts
          const accountsRes = await fetch(`${API_URL}/accounts/all`);
          const accountsData = await accountsRes.json();
          setAllAccounts(accountsData.accounts || []);
          
          // Fetch client's current access
          const accessRes = await fetch(`${API_URL}/clients/${client.id}/accounts`);
          const accessData = await accessRes.json();
          setSelectedAccountIds(new Set(accessData.account_ids || []));
        } catch (error) {
          console.error('Error fetching data:', error);
          if (addToast) addToast('Failed to load accounts', 'error');
        }
        setLoading(false);
      };
      fetchData();
    }
  }, [isOpen, client, addToast]);

  // Filter accounts based on search and platform
  const filteredAccounts = useMemo(() => {
    return allAccounts.filter(acc => {
      const matchesSearch = acc.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPlatform = platformFilter === 'all' || acc.platform === platformFilter;
      return matchesSearch && matchesPlatform;
    });
  }, [allAccounts, searchTerm, platformFilter]);

  // Group accounts by platform
  const groupedAccounts = useMemo(() => {
    const groups = { META: [], GOOGLE: [] };
    filteredAccounts.forEach(acc => {
      if (groups[acc.platform]) {
        groups[acc.platform].push(acc);
      }
    });
    return groups;
  }, [filteredAccounts]);

  // Toggle account selection
  const toggleAccount = (accountId) => {
    setSelectedAccountIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  // Select/Deselect all visible accounts
  const toggleAll = (select) => {
    if (select) {
      setSelectedAccountIds(prev => {
        const newSet = new Set(prev);
        filteredAccounts.forEach(acc => newSet.add(acc.id));
        return newSet;
      });
    } else {
      setSelectedAccountIds(prev => {
        const newSet = new Set(prev);
        filteredAccounts.forEach(acc => newSet.delete(acc.id));
        return newSet;
      });
    }
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/clients/${client.id}/accounts`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_ids: Array.from(selectedAccountIds) })
      });
      
      if (response.ok) {
        if (addToast) addToast('Account access updated successfully', 'success');
        onUpdate();
        onClose();
      } else {
        const error = await response.json();
        if (addToast) addToast(error.detail || 'Failed to update access', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to update access', 'error');
    }
    setSaving(false);
  };

  const metaCount = groupedAccounts.META.length;
  const googleCount = groupedAccounts.GOOGLE.length;
  const selectedMetaCount = groupedAccounts.META.filter(a => selectedAccountIds.has(a.id)).length;
  const selectedGoogleCount = groupedAccounts.GOOGLE.filter(a => selectedAccountIds.has(a.id)).length;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Manage Accounts - ${client?.name}`} size="lg">
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#6366f1]/20 rounded-full animate-spin border-t-[#6366f1]"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search accounts..."
                className="w-full pl-10 pr-4 py-2.5 bg-[#18181B] border border-white/10 rounded-xl text-white placeholder-zinc-500 outline-none focus:border-[#6366f1] text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPlatformFilter('all')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  platformFilter === 'all' 
                    ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30' 
                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setPlatformFilter('META')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  platformFilter === 'META' 
                    ? 'bg-[#1877F2]/20 text-[#1877F2] border border-[#1877F2]/30' 
                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                Meta ({metaCount})
              </button>
              <button
                onClick={() => setPlatformFilter('GOOGLE')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  platformFilter === 'GOOGLE' 
                    ? 'bg-[#4285F4]/20 text-[#4285F4] border border-[#4285F4]/30' 
                    : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                }`}
              >
                Google ({googleCount})
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-zinc-400">
              <span className="text-[#22c55e] font-bold">{selectedAccountIds.size}</span> accounts selected
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => toggleAll(true)}
                className="px-3 py-1.5 text-xs font-medium text-[#6366f1] hover:bg-[#6366f1]/10 rounded-lg transition-colors"
              >
                Select All Visible
              </button>
              <button
                onClick={() => toggleAll(false)}
                className="px-3 py-1.5 text-xs font-medium text-zinc-400 hover:bg-white/10 rounded-lg transition-colors"
              >
                Deselect All Visible
              </button>
            </div>
          </div>

          {/* Account List */}
          <div className="max-h-[400px] overflow-y-auto space-y-4 pr-1">
            {/* Meta Accounts */}
            {groupedAccounts.META.length > 0 && (platformFilter === 'all' || platformFilter === 'META') && (
              <div>
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#09090B] py-1">
                  <div className="w-6 h-6 rounded bg-[#1877F2]/20 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-[#1877F2]">M</span>
                  </div>
                  <h4 className="text-sm font-semibold text-white">Meta Accounts</h4>
                  <span className="text-xs text-zinc-500">({selectedMetaCount}/{metaCount} selected)</span>
                </div>
                <div className="grid gap-2">
                  {groupedAccounts.META.map(account => (
                    <label
                      key={account.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        selectedAccountIds.has(account.id)
                          ? 'bg-[#1877F2]/10 border-[#1877F2]/30'
                          : 'bg-[#18181B] border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAccountIds.has(account.id)}
                        onChange={() => toggleAccount(account.id)}
                        className="w-4 h-4 rounded border-white/20 bg-[#18181B] text-[#1877F2] focus:ring-[#1877F2]/20"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{account.name}</p>
                        <p className="text-xs text-zinc-500">{account.account_id}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        account.status === 'ACTIVE' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {account.status || 'N/A'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Google Accounts */}
            {groupedAccounts.GOOGLE.length > 0 && (platformFilter === 'all' || platformFilter === 'GOOGLE') && (
              <div>
                <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#09090B] py-1">
                  <div className="w-6 h-6 rounded bg-[#4285F4]/20 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-[#4285F4]">G</span>
                  </div>
                  <h4 className="text-sm font-semibold text-white">Google Accounts</h4>
                  <span className="text-xs text-zinc-500">({selectedGoogleCount}/{googleCount} selected)</span>
                </div>
                <div className="grid gap-2">
                  {groupedAccounts.GOOGLE.map(account => (
                    <label
                      key={account.id}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                        selectedAccountIds.has(account.id)
                          ? 'bg-[#4285F4]/10 border-[#4285F4]/30'
                          : 'bg-[#18181B] border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAccountIds.has(account.id)}
                        onChange={() => toggleAccount(account.id)}
                        className="w-4 h-4 rounded border-white/20 bg-[#18181B] text-[#4285F4] focus:ring-[#4285F4]/20"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{account.name}</p>
                        <p className="text-xs text-zinc-500">{account.account_id}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        account.status === 'ENABLED' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-zinc-500/20 text-zinc-400'
                      }`}>
                        {account.status || 'N/A'}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {filteredAccounts.length === 0 && (
              <div className="text-center py-8">
                <p className="text-zinc-500 text-sm">No accounts found matching your search</p>
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

// Returns true if a last_synced_at ISO string is older than 24 hours (or null)
const isStaleSyncTime = (lastSyncedAt) => {
  if (!lastSyncedAt) return true;
  const synced = new Date(lastSyncedAt);
  return (Date.now() - synced.getTime()) > 24 * 60 * 60 * 1000;
};

// Lead Sheets Tab Component
const LeadSheetsTab = ({ addToast }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [syncLogs, setSyncLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [linkingAccount, setLinkingAccount] = useState(null);
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountLogs, setAccountLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null); // config id being edited
  const [editForm, setEditForm] = useState({ sync_frequency_hours: 4, spreadsheet_url: '', relinking: false });
  const [deletingConfig, setDeletingConfig] = useState(null); // config id pending delete confirm
  const [syncStatus, setSyncStatus] = useState({ accounts: [], stale_count: 0, last_global_sync: null });

  // Fetch accounts with sheet status (active in last 30 days)
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/sheets/accounts?active_only=true`);
      const data = await response.json();
      setAccounts(data.accounts || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      if (addToast) addToast('Failed to load accounts', 'error');
    }
    setLoading(false);
  }, [addToast]);

  // Fetch per-account sync status from the new endpoint
  const fetchSyncStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/sheets/sync-status`);
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  }, []);

  // Fetch sync logs
  const fetchLogs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/sheets/logs?limit=20`);
      const data = await response.json();
      setSyncLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  }, []);

  // Fetch logs for a specific account
  const fetchAccountLogs = useCallback(async (configId) => {
    setLoadingLogs(true);
    try {
      const response = await fetch(`${API_URL}/sheets/configs/${configId}/logs?limit=20`);
      const data = await response.json();
      setAccountLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching account logs:', error);
    }
    setLoadingLogs(false);
  }, []);

  useEffect(() => {
    fetchAccounts();
    fetchLogs();
    fetchSyncStatus();
    // Kick off auto-creation of any missing sheets on load
    fetch(`${API_URL}/sheets/auto-setup`, { method: 'POST' }).catch(() => {});
  }, [fetchAccounts, fetchLogs, fetchSyncStatus]);

  // Filter accounts by search term
  const filteredAccounts = useMemo(() => {
    if (!searchTerm) return accounts;
    const search = searchTerm.toLowerCase();
    return accounts.filter(a => a.name.toLowerCase().includes(search));
  }, [accounts, searchTerm]);

  // Handle account click for logs
  const handleAccountClick = (account) => {
    if (selectedAccount?.id === account.id) {
      setSelectedAccount(null);
      setAccountLogs([]);
    } else {
      setSelectedAccount(account);
      if (account.sheet_config?.id) {
        fetchAccountLogs(account.sheet_config.id);
      }
    }
  };

  // Link existing spreadsheet
  const handleLinkSpreadsheet = async (accountId) => {
    if (!spreadsheetUrl.trim()) {
      if (addToast) addToast('Please enter a spreadsheet URL', 'error');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/sheets/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          account_id: accountId,
          spreadsheet_url: spreadsheetUrl 
        })
      });
      
      if (response.ok) {
        if (addToast) addToast('Spreadsheet linked successfully!', 'success');
        setLinkingAccount(null);
        setSpreadsheetUrl('');
        setTimeout(fetchAccounts, 2000);
      } else {
        const error = await response.json();
        if (addToast) addToast(error.detail || 'Failed to link spreadsheet', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to link spreadsheet', 'error');
    }
    setSubmitting(false);
  };

  // Trigger sync by account_id using the new sync-now endpoint
  const handleSyncByAccountId = async (accountId, configId) => {
    setSyncing(prev => ({ ...prev, [configId]: true }));
    try {
      const response = await fetch(`${API_URL}/sheets/sync-now/${accountId}`, {
        method: 'POST'
      });

      if (response.ok) {
        if (addToast) addToast('Sync started!', 'success');
        setTimeout(() => {
          fetchAccounts();
          fetchLogs();
          fetchSyncStatus();
        }, 5000);
      } else {
        const err = await response.json();
        if (addToast) addToast(err.detail || 'Failed to start sync', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to start sync', 'error');
    }
    setSyncing(prev => ({ ...prev, [configId]: false }));
  };

  // Toggle sheet enabled status
  const handleToggleEnabled = async (configId, currentStatus) => {
    try {
      await fetch(`${API_URL}/sheets/configs/${configId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_enabled: !currentStatus })
      });
      fetchAccounts();
    } catch (error) {
      if (addToast) addToast('Failed to update', 'error');
    }
  };

  // Sync all
  const handleSyncAll = async () => {
    try {
      const response = await fetch(`${API_URL}/sheets/sync-all`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        if (addToast) addToast(data.message, 'success');
        setTimeout(() => {
          fetchAccounts();
          fetchLogs();
          fetchSyncStatus();
        }, 5000);
      }
    } catch (error) {
      if (addToast) addToast('Failed to sync all', 'error');
    }
  };

  // Open edit panel for a config
  const handleOpenEdit = (e, account) => {
    e.stopPropagation();
    setEditingConfig(account.sheet_config.id);
    setEditForm({
      sync_frequency_hours: account.sheet_config.sync_frequency_hours || 4,
      spreadsheet_url: '',
      relinking: false,
    });
  };

  // Save config edits
  const handleSaveEdit = async (configId) => {
    setSubmitting(true);
    try {
      const body = { sync_frequency_hours: editForm.sync_frequency_hours };
      if (editForm.relinking && editForm.spreadsheet_url.trim()) {
        body.spreadsheet_url = editForm.spreadsheet_url.trim();
      }
      const res = await fetch(`${API_URL}/sheets/configs/${configId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        if (addToast) addToast('Settings saved', 'success');
        setEditingConfig(null);
        fetchAccounts();
      } else {
        const err = await res.json();
        if (addToast) addToast(err.detail || 'Save failed', 'error');
      }
    } catch {
      if (addToast) addToast('Save failed', 'error');
    }
    setSubmitting(false);
  };

  // Delete / unlink config
  const handleDeleteConfig = async (configId) => {
    try {
      const res = await fetch(`${API_URL}/sheets/configs/${configId}`, { method: 'DELETE' });
      if (res.ok) {
        if (addToast) addToast('Sheet unlinked', 'success');
        setDeletingConfig(null);
        setEditingConfig(null);
        fetchAccounts();
      } else {
        if (addToast) addToast('Failed to unlink', 'error');
      }
    } catch {
      if (addToast) addToast('Failed to unlink', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#6366f1]/20 rounded-full animate-spin border-t-[#6366f1]"></div>
      </div>
    );
  }

  const accountsWithSheets = filteredAccounts.filter(a => a.has_sheet_config);
  const accountsWithoutSheets = filteredAccounts.filter(a => !a.has_sheet_config);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <BarChart2 className="w-6 h-6 text-[#6366f1]" /> Lead Sheets Automation
          </h2>
          <p className="text-zinc-500 text-sm mt-1">Automatically sync Meta leads to Google Sheets (Active accounts in last 30 days)</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowLogs(!showLogs)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
              showLogs ? 'bg-[#6366f1]/20 text-[#6366f1]' : 'bg-white/5 text-zinc-400 hover:bg-white/10'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Logs
          </button>
          {accountsWithSheets.length > 0 && (
            <button
              onClick={handleSyncAll}
              className="px-4 py-2 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sync All
            </button>
          )}
        </div>
      </div>

      {/* Search Box */}
      <div className="relative">
        <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-[#09090B] border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-[#6366f1]/50 focus:ring-1 focus:ring-[#6366f1]/50"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Active Accounts (30d)</p>
          <p className="text-2xl font-bold text-white mt-1">{accounts.length}</p>
        </div>
        <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">With Sheets</p>
          <p className="text-2xl font-bold text-[#22c55e] mt-1">{accounts.filter(a => a.has_sheet_config).length}</p>
        </div>
        <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Leads Synced</p>
          <p className="text-2xl font-bold text-[#6366f1] mt-1">
            {accounts.filter(a => a.has_sheet_config).reduce((sum, a) => sum + (a.sheet_config?.total_leads_synced || 0), 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
          <p className="text-xs text-zinc-500 uppercase tracking-wider">Pending Setup</p>
          <p className="text-2xl font-bold text-yellow-400 mt-1">{accounts.filter(a => !a.has_sheet_config).length}</p>
        </div>
      </div>

      {/* Stale Sync Warning Banner */}
      {syncStatus.stale_count > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-yellow-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-yellow-300">
                {syncStatus.stale_count} account{syncStatus.stale_count !== 1 ? 's' : ''} not synced in 24h
              </p>
              {syncStatus.last_global_sync && (
                <p className="text-xs text-yellow-500/80 font-mono mt-0.5">
                  Last global sync: {formatToIST(syncStatus.last_global_sync)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleSyncAll}
            className="px-3 py-2 bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500/40 rounded-lg text-xs font-bold text-yellow-300 transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Sync All Stale
          </button>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-[#6366f1]/10 border border-[#6366f1]/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-[#6366f1] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm text-white font-medium">Fully Automatic Lead Sheets</p>
            <p className="text-xs text-zinc-400 mt-1">
              Google Sheets are created automatically for all active Meta accounts. No manual setup needed.
              Sheets sync every 4 hours with one tab per campaign. Click <span className="text-[#22c55e] font-medium">Open Sheet</span> to view.
            </p>
          </div>
        </div>
      </div>

      {/* Sync Logs */}
      {showLogs && (
        <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
          <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-[#6366f1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Recent Sync Logs
          </h3>
          {syncLogs.length > 0 ? (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {syncLogs.map(log => (
                <div key={log.id} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                  <div className="flex items-center gap-3">
                    <span className={`w-2 h-2 rounded-full ${
                      log.status === 'success' ? 'bg-[#22c55e]' : 
                      log.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400'
                    }`}></span>
                    <div>
                      <p className="text-sm text-white">{log.account_name}</p>
                      <p className="text-xs text-zinc-500">
                        {log.leads_synced} leads, {log.campaigns_synced} campaigns
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-400">
                      {log.completed_at ? formatToIST(log.completed_at) : 'In progress...'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500 text-center py-4">No sync logs yet</p>
          )}
        </div>
      )}

      {/* Accounts with Sheets */}
      {accountsWithSheets.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">
            Active Lead Sheets ({accountsWithSheets.length})
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            {accountsWithSheets.map(account => (
              <div key={account.id} className={`bg-[#09090B] rounded-xl border p-4 transition-colors cursor-pointer ${
                selectedAccount?.id === account.id ? 'border-[#6366f1] ring-2 ring-[#6366f1]/20' : 'border-white/10 hover:border-white/20'
              }`}>
                <div className="flex items-start justify-between mb-3" onClick={() => handleAccountClick(account)}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#1877F2]/20 flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-white text-sm">{account.name}</h4>
                      <p className="text-xs text-zinc-500">
                        {account.sheet_config?.total_leads_synced?.toLocaleString() || 0} leads synced
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={account.sheet_config?.is_enabled}
                      onChange={() => handleToggleEnabled(account.sheet_config?.id, account.sheet_config?.is_enabled)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-colors peer-checked:bg-[#22c55e]"></div>
                  </label>
                </div>
                
                {/* Status */}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {!account.sheet_config?.spreadsheet_id ? (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-orange-500/20 text-orange-400">
                      Creating Sheet...
                    </span>
                  ) : (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      account.sheet_config?.last_sync_status === 'success' ? 'bg-[#22c55e]/20 text-[#22c55e]' :
                      account.sheet_config?.last_sync_status === 'failed' ? 'bg-red-500/20 text-red-400' :
                      account.sheet_config?.last_sync_status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-zinc-500/20 text-zinc-400'
                    }`}>
                      {account.sheet_config?.last_sync_status || 'Ready'}
                    </span>
                  )}
                  {isStaleSyncTime(account.sheet_config?.last_synced_at) && account.sheet_config?.spreadsheet_id && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                      Stale &gt;24h
                    </span>
                  )}
                  {account.sheet_config?.last_synced_at && (
                    <span className="text-[10px] text-zinc-500 font-mono tracking-wide">
                      {formatToIST(account.sheet_config.last_synced_at)}
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAccountClick(account); }}
                    className="ml-auto text-[10px] text-[#6366f1] hover:underline"
                  >
                    {selectedAccount?.id === account.id ? 'Hide Logs' : 'View Logs'}
                  </button>
                </div>

                {/* Per-Account Sync Logs */}
                {selectedAccount?.id === account.id && (
                  <div className="mb-3 p-3 bg-[#18181B] rounded-lg">
                    <h5 className="text-xs font-bold text-zinc-400 mb-2">Sync History</h5>
                    {loadingLogs ? (
                      <div className="flex justify-center py-2">
                        <div className="w-4 h-4 border-2 border-[#6366f1]/20 rounded-full animate-spin border-t-[#6366f1]"></div>
                      </div>
                    ) : accountLogs.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {accountLogs.map((log, idx) => (
                          <div key={log.id || idx} className="flex items-center justify-between text-[10px] py-1 border-b border-white/5 last:border-0">
                            <div className="flex items-center gap-2">
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                log.status === 'success' ? 'bg-[#22c55e]' : 
                                log.status === 'failed' ? 'bg-red-400' : 'bg-yellow-400'
                              }`}></span>
                              <span className="text-zinc-400">{log.leads_synced} leads</span>
                              {log.campaigns_synced > 0 && (
                                <span className="text-zinc-500">({log.campaigns_synced} campaigns)</span>
                              )}
                            </div>
                            <span className="text-zinc-500">
                              {log.completed_at ? formatToIST(log.completed_at) : 'In progress...'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[10px] text-zinc-500 text-center py-2">No sync history yet</p>
                    )}
                  </div>
                )}

                {/* Error Message if failed */}
                {account.sheet_config?.last_sync_error && (
                  <div className="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-[10px] text-red-400">{account.sheet_config.last_sync_error}</p>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  {account.sheet_config?.spreadsheet_url ? (
                    <a
                      href={account.sheet_config.spreadsheet_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 px-3 py-2 bg-[#34A853]/10 hover:bg-[#34A853]/20 rounded-lg text-xs text-[#34A853] font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.385 2H4.615A2.615 2.615 0 002 4.615v14.77A2.615 2.615 0 004.615 22h14.77A2.615 2.615 0 0022 19.385V4.615A2.615 2.615 0 0019.385 2zM8 18H5v-3h3v3zm0-5H5v-3h3v3zm0-5H5V5h3v3zm11 10h-9v-3h9v3zm0-5h-9v-3h9v3zm0-5h-9V5h9v3z"/>
                      </svg>
                      Open Sheet
                    </a>
                  ) : (
                    <span className="flex-1 px-3 py-2 bg-orange-500/10 rounded-lg text-xs text-orange-400 text-center flex items-center justify-center gap-1.5">
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating sheet...
                    </span>
                  )}
                  {account.sheet_config?.spreadsheet_id && (
                    <button
                      onClick={() => handleSyncByAccountId(account.id, account.sheet_config?.id)}
                      disabled={syncing[account.sheet_config?.id]}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-2 disabled:opacity-50 ${
                        isStaleSyncTime(account.sheet_config?.last_synced_at)
                          ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/30'
                          : 'bg-[#6366f1]/10 hover:bg-[#6366f1]/20 text-[#6366f1]'
                      }`}
                    >
                      {syncing[account.sheet_config?.id] ? (
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                      Sync Now
                    </button>
                  )}
                  {/* Settings / Edit button */}
                  <button
                    onClick={(e) => editingConfig === account.sheet_config?.id ? setEditingConfig(null) : handleOpenEdit(e, account)}
                    className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5 ${
                      editingConfig === account.sheet_config?.id
                        ? 'bg-zinc-600 text-white'
                        : 'bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white'
                    }`}
                    title="Edit settings"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                </div>

                {/* Edit Settings Panel */}
                {editingConfig === account.sheet_config?.id && (
                  <div className="mt-3 p-3 bg-[#18181B] rounded-xl border border-white/10 space-y-3">
                    <p className="text-xs font-bold text-zinc-300 flex items-center gap-2">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Sheet Settings
                    </p>

                    {/* Sync Frequency */}
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Sync Frequency</label>
                      <select
                        value={editForm.sync_frequency_hours}
                        onChange={(e) => setEditForm(f => ({ ...f, sync_frequency_hours: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 bg-[#09090B] border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-[#6366f1]/50"
                      >
                        <option value={1}>Every 1 hour</option>
                        <option value={4}>Every 4 hours (default)</option>
                        <option value={12}>Every 12 hours</option>
                        <option value={24}>Once daily</option>
                      </select>
                    </div>

                    {/* Re-link spreadsheet */}
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 flex items-center gap-2">
                        Change Spreadsheet
                        <button
                          onClick={() => setEditForm(f => ({ ...f, relinking: !f.relinking, spreadsheet_url: '' }))}
                          className="text-[#6366f1] hover:underline normal-case"
                        >
                          {editForm.relinking ? 'Cancel' : 'Change'}
                        </button>
                      </label>
                      {editForm.relinking ? (
                        <input
                          type="text"
                          placeholder="Paste new Google Sheet URL..."
                          value={editForm.spreadsheet_url}
                          onChange={(e) => setEditForm(f => ({ ...f, spreadsheet_url: e.target.value }))}
                          className="w-full px-3 py-2 bg-[#09090B] border border-white/10 rounded-lg text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-[#6366f1]/50"
                        />
                      ) : (
                        <p className="text-[10px] text-zinc-600 truncate">{account.sheet_config?.spreadsheet_url || 'No sheet linked'}</p>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        onClick={() => handleSaveEdit(account.sheet_config?.id)}
                        disabled={submitting}
                        className="flex-1 px-3 py-2 bg-[#6366f1] hover:bg-[#6366f1]/80 text-white rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
                      >
                        {submitting ? 'Saving...' : 'Save Changes'}
                      </button>
                      {deletingConfig === account.sheet_config?.id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleDeleteConfig(account.sheet_config?.id)}
                            className="px-3 py-2 bg-red-500 text-white rounded-lg text-xs font-bold transition-colors"
                          >
                            Confirm Unlink
                          </button>
                          <button
                            onClick={() => setDeletingConfig(null)}
                            className="px-3 py-2 bg-white/10 text-zinc-400 rounded-lg text-xs transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeletingConfig(account.sheet_config?.id)}
                          className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-medium transition-colors flex items-center gap-1.5"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Unlink
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accounts without Sheets */}
      {accountsWithoutSheets.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">
            Available Accounts ({accountsWithoutSheets.length})
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {accountsWithoutSheets.map(account => (
              <div key={account.id} className="bg-[#09090B] rounded-xl border border-white/10 p-4 hover:border-white/20 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#1877F2]/10 flex items-center justify-center">
                      <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-white text-sm truncate block">{account.name}</span>
                      <span className="text-[10px] text-zinc-500">{account.leads_30d || 0} leads (30d) • {formatCurrency(account.spend_30d || 0, account.currency)} spend</span>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={async () => {
                    setSyncing(prev => ({ ...prev, [account.id]: true }));
                    try {
                      const res = await fetch(`${API_URL}/sheets/configs`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ account_id: account.id, sync_frequency_hours: 4 })
                      });
                      if (res.ok) {
                        if (addToast) addToast('Sheet creation started — refresh in a moment', 'success');
                        setTimeout(fetchAccounts, 5000);
                      } else {
                        const err = await res.json();
                        if (addToast) addToast(err.detail || 'Failed to create sheet', 'error');
                      }
                    } catch {
                      if (addToast) addToast('Failed to create sheet', 'error');
                    }
                    setSyncing(prev => ({ ...prev, [account.id]: false }));
                  }}
                  disabled={syncing[account.id]}
                  className="w-full px-3 py-2 bg-[#6366f1] text-white rounded-lg text-xs font-bold hover:opacity-90 transition-colors flex items-center justify-center gap-1 disabled:opacity-50"
                >
                  {syncing[account.id] ? (
                    <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  ) : (
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.385 2H4.615A2.615 2.615 0 002 4.615v14.77A2.615 2.615 0 004.615 22h14.77A2.615 2.615 0 0022 19.385V4.615A2.615 2.615 0 0019.385 2zM8 18H5v-3h3v3zm0-5H5v-3h3v3zm0-5H5V5h3v3zm11 10h-9v-3h9v3zm0-5h-9v-3h9v3zm0-5h-9V5h9v3z"/>
                    </svg>
                  )}
                  Create Sheet
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {accounts.length === 0 && (
        <div className="bg-[#09090B] rounded-2xl border border-white/10 p-12 text-center">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">No Meta Accounts Found</h3>
          <p className="text-zinc-500 text-sm">Sync your Meta accounts first to set up lead sheets</p>
        </div>
      )}
    </div>
  );
};

// Client Card Component
const ClientCard = ({ client, onEdit, onDelete, onResetPassword, onManageAccounts, addToast }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [password, setPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  
  // Handle set password
  const handleSetPassword = async () => {
    if (!password || password.length < 4) {
      if (addToast) addToast('Password must be at least 4 characters', 'error');
      return;
    }
    
    setSettingPassword(true);
    try {
      const response = await fetch(`${API_URL}/clients/${client.id}/set-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      
      if (response.ok) {
        setPassword('');
        if (addToast) addToast(`Password set for ${client.email}`, 'success');
      } else {
        const error = await response.json();
        if (addToast) addToast(error.detail || 'Failed to set password', 'error');
      }
    } catch (error) {
      console.error('Error setting password:', error);
      if (addToast) addToast('Failed to set password', 'error');
    }
    setSettingPassword(false);
  };
  
  return (
    <div className="bg-[#09090B] rounded-xl sm:rounded-2xl border border-white/10 p-3 sm:p-5 hover:border-white/20 transition-colors">
      <div className="flex items-start justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-[#6366f1]/20 to-[#22c55e]/10 flex items-center justify-center flex-shrink-0">
            <span className="text-lg sm:text-xl font-bold text-[#22c55e]">{client.name.charAt(0).toUpperCase()}</span>
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-white text-sm sm:text-base truncate">{client.name}</h3>
            <p className="text-xs sm:text-sm text-zinc-500 truncate">{client.email}</p>
            {client.company_name && (
              <p className="text-[10px] sm:text-xs text-zinc-600 mt-0.5 truncate">{client.company_name}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-bold uppercase ${
            client.is_active ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'bg-red-500/20 text-red-400'
          }`}>
            {client.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>
      
      {/* Set Password Section */}
      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/5">
        <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider mb-2">Set Password</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full px-3 py-2 bg-[#18181B] border border-white/10 rounded-lg text-xs sm:text-sm text-white placeholder-zinc-500 outline-none focus:border-[#6366f1] transition-colors"
            />
          </div>
          <button
            onClick={handleSetPassword}
            disabled={settingPassword || !password}
            className="px-3 sm:px-4 py-2 bg-[#6366f1] text-white rounded-lg text-xs sm:text-sm font-bold hover:opacity-90 transition-colors disabled:opacity-50 flex items-center gap-2 min-h-[38px]"
          >
            {settingPassword ? (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Set'
            )}
          </button>
        </div>
      </div>
      
      {/* Account Access */}
      <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/5">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">Account Access</p>
          <button
            onClick={onManageAccounts}
            className="text-[10px] sm:text-xs text-[#6366f1] hover:text-[#22c55e] font-medium flex items-center gap-1 transition-colors"
          >
            <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Manage
          </button>
        </div>
        
        {client.accounts && client.accounts.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {client.accounts.slice(0, showDetails ? client.accounts.length : 3).map(acc => (
              <span key={acc.id} className={`px-2 py-1 rounded-lg text-[10px] sm:text-xs font-medium truncate max-w-[100px] sm:max-w-none ${
                acc.platform === 'META' ? 'bg-[#1877F2]/20 text-[#1877F2]' : 'bg-[#4285F4]/20 text-[#4285F4]'
              }`}>
                {acc.name}
              </span>
            ))}
            {!showDetails && client.accounts.length > 3 && (
              <button
                onClick={() => setShowDetails(true)}
                className="px-2 py-1 rounded-lg text-[10px] sm:text-xs font-medium text-zinc-400 hover:text-white transition-colors"
              >
                +{client.accounts.length - 3}
              </button>
            )}
          </div>
        ) : (
          <button
            onClick={onManageAccounts}
            className="w-full py-2.5 sm:py-3 border-2 border-dashed border-white/10 rounded-xl text-[10px] sm:text-xs text-zinc-500 hover:border-[#6366f1]/30 hover:text-[#6366f1] transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Accounts
          </button>
        )}
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1.5 sm:gap-2 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/5">
        <button
          onClick={onManageAccounts}
          className="flex-1 px-2 sm:px-3 py-2 bg-[#6366f1]/10 hover:bg-[#6366f1]/20 rounded-lg text-[10px] sm:text-sm text-[#6366f1] font-medium transition-colors flex items-center justify-center gap-1 sm:gap-2 min-h-[38px]"
        >
          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <span className="hidden xs:inline">Accounts</span>
        </button>
        <button
          onClick={onEdit}
          className="px-2.5 sm:px-3 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-zinc-300 font-medium transition-colors min-h-[38px]"
        >
          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
        <button
          onClick={onResetPassword}
          className="px-2.5 sm:px-3 py-2 bg-yellow-500/10 hover:bg-yellow-500/20 rounded-lg text-sm text-yellow-400 font-medium transition-colors min-h-[38px]"
        >
          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </button>
        <button
          onClick={onDelete}
          className="px-2.5 sm:px-3 py-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-sm text-red-400 font-medium transition-colors min-h-[38px]"
        >
          <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      
      {/* Last Login */}
      {client.last_login && (
        <p className="text-[10px] text-zinc-600 mt-3">
          Last login: {formatToIST(client.last_login)}
        </p>
      )}
    </div>
  );
};

// Main Component
const ClientPortal = ({ addToast }) => {
  const [activeTab, setActiveTab] = useState('clients');
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [tempPassword, setTempPassword] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: ''
  });
  
  // Fetch clients
  const fetchClients = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/clients`);
      const data = await response.json();
      setClients(data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      if (addToast) addToast('Failed to load clients', 'error');
    }
    setLoading(false);
  }, [addToast]);
  
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);
  
  // Create client
  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setShowCreateModal(false);
        setFormData({ email: '', password: '', name: '', company_name: '' });
        fetchClients();
        if (addToast) addToast('Client created successfully', 'success');
      } else {
        const error = await response.json();
        if (addToast) addToast(error.detail || 'Failed to create client', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to create client', 'error');
    }
  };
  
  // Update client
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    
    try {
      const response = await fetch(`${API_URL}/clients/${selectedClient.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          company_name: formData.company_name,
          is_active: formData.is_active
        })
      });
      
      if (response.ok) {
        setShowEditModal(false);
        setSelectedClient(null);
        fetchClients();
        if (addToast) addToast('Client updated successfully', 'success');
      } else {
        if (addToast) addToast('Failed to update client', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to update client', 'error');
    }
  };
  
  // Delete client
  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${API_URL}/clients/${clientId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchClients();
        if (addToast) addToast('Client deleted successfully', 'success');
      } else {
        if (addToast) addToast('Failed to delete client', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to delete client', 'error');
    }
  };
  
  // Reset password
  const handleResetPassword = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/clients/${clientId}/reset-password`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        setTempPassword(data.temporary_password);
        if (addToast) addToast('Password reset successful', 'success');
      } else {
        if (addToast) addToast('Failed to reset password', 'error');
      }
    } catch (error) {
      if (addToast) addToast('Failed to reset password', 'error');
    }
  };
  
  // Open edit modal
  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      email: client.email,
      name: client.name,
      company_name: client.company_name || '',
      is_active: client.is_active
    });
    setShowEditModal(true);
  };

  // Open accounts modal
  const openAccountsModal = (client) => {
    setSelectedClient(client);
    setShowAccountsModal(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#6366f1]/20 rounded-full animate-spin border-t-[#6366f1]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Tabs */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Users className="w-7 h-7 text-[#6366f1]" /> Client Portal
            </h1>
            <p className="text-zinc-500 text-sm mt-1">Manage client access and lead sheets</p>
          </div>
          {activeTab === 'clients' && (
            <button
              onClick={() => {
                setFormData({ email: '', password: '', name: '', company_name: '' });
                setShowCreateModal(true);
              }}
              className="px-4 py-2.5 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors shadow-lg shadow-[#6366f1]/20 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Client
            </button>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b border-white/10 pb-2">
          <button
            onClick={() => setActiveTab('clients')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'clients'
                ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30'
                : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Clients
          </button>
          <button
            onClick={() => setActiveTab('leadsheets')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'leadsheets'
                ? 'bg-[#34A853]/20 text-[#34A853] border border-[#34A853]/30'
                : 'text-zinc-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.385 2H4.615A2.615 2.615 0 002 4.615v14.77A2.615 2.615 0 004.615 22h14.77A2.615 2.615 0 0022 19.385V4.615A2.615 2.615 0 0019.385 2zM8 18H5v-3h3v3zm0-5H5v-3h3v3zm0-5H5V5h3v3zm11 10h-9v-3h9v3zm0-5h-9v-3h9v3zm0-5h-9V5h9v3z"/>
            </svg>
            Lead Sheets
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'leadsheets' ? (
        <LeadSheetsTab addToast={addToast} />
      ) : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Clients</p>
              <p className="text-2xl font-bold text-white mt-1">{clients.length}</p>
            </div>
            <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Active</p>
              <p className="text-2xl font-bold text-[#22c55e] mt-1">{clients.filter(c => c.is_active).length}</p>
            </div>
            <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">With Accounts</p>
              <p className="text-2xl font-bold text-[#6366f1] mt-1">{clients.filter(c => c.accounts_count > 0).length}</p>
            </div>
            <div className="bg-[#09090B] rounded-xl border border-white/10 p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Access</p>
              <p className="text-2xl font-bold text-white mt-1">{clients.reduce((sum, c) => sum + (c.accounts_count || 0), 0)}</p>
            </div>
          </div>
          
          {/* Info Box */}
          <div className="bg-[#6366f1]/10 border border-[#6366f1]/30 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-[#6366f1] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-white font-medium">How Client Access Works</p>
                <ul className="text-xs text-zinc-400 mt-1 space-y-0.5">
                  <li>• Create a client with email/password for login access</li>
                  <li>• Click <span className="text-[#6366f1] font-medium">"Accounts"</span> or <span className="text-[#6366f1] font-medium">"Manage"</span> to assign ad accounts to each client</li>
                  <li>• Clients can login at <code className="px-1 py-0.5 bg-white/10 rounded text-[#22c55e]">/client</code> to view their assigned accounts</li>
                </ul>
              </div>
            </div>
          </div>
      
      {/* Client Search */}
      <div className="relative mb-4">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
        <input
          type="text"
          value={clientSearch}
          onChange={e => setClientSearch(e.target.value)}
          placeholder="Search clients by name, email or company..."
          className="w-full pl-9 pr-4 py-2.5 bg-[#09090B] border border-white/10 rounded-lg text-sm text-white placeholder-zinc-500 outline-none focus:border-[#6366f1]/50"
        />
        {clientSearch && (
          <button onClick={() => setClientSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        )}
      </div>

      {/* Clients Grid */}
      {clients.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {clients.filter(c => {
            if (!clientSearch.trim()) return true;
            const q = clientSearch.toLowerCase();
            return (c.name||'').toLowerCase().includes(q) || (c.email||'').toLowerCase().includes(q) || (c.company_name||'').toLowerCase().includes(q);
          }).map(client => (
            <ClientCard
              key={client.id}
              client={client}
              onEdit={() => openEditModal(client)}
              onDelete={() => handleDelete(client.id)}
              onResetPassword={() => handleResetPassword(client.id)}
              onManageAccounts={() => openAccountsModal(client)}
              addToast={addToast}
            />
          ))}
        </div>
      ) : (
        <div className="bg-[#09090B] rounded-2xl border border-white/10 p-12 text-center">
          <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-zinc-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">No Clients Yet</h3>
          <p className="text-zinc-500 text-sm mb-6">Create your first client to give them access to their dashboard</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors"
          >
            Create First Client
          </button>
        </div>
      )}
        </>
      )}
      
      {/* Create Modal */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Client">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(v) => setFormData({ ...formData, name: v })}
            placeholder="John Doe"
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(v) => setFormData({ ...formData, email: v })}
            placeholder="john@example.com"
            required
          />
          <Input
            label="Password"
            type="password"
            value={formData.password}
            onChange={(v) => setFormData({ ...formData, password: v })}
            placeholder="Create a password"
            required
          />
          <Input
            label="Company Name"
            value={formData.company_name}
            onChange={(v) => setFormData({ ...formData, company_name: v })}
            placeholder="Acme Inc. (optional)"
          />
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors"
            >
              Create Client
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Edit Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Client">
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(v) => setFormData({ ...formData, name: v })}
            placeholder="John Doe"
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(v) => setFormData({ ...formData, email: v })}
            placeholder="john@example.com"
            required
          />
          <Input
            label="Company Name"
            value={formData.company_name}
            onChange={(v) => setFormData({ ...formData, company_name: v })}
            placeholder="Acme Inc. (optional)"
          />
          
          <div className="flex items-center gap-3 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-white/20 bg-[#18181B] text-[#6366f1] focus:ring-[#6366f1]/20"
              />
              <span className="text-sm text-zinc-300">Active</span>
            </label>
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              className="flex-1 px-4 py-3 bg-white/5 hover:bg-white/10 rounded-xl text-white font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </Modal>
      
      {/* Account Access Modal */}
      <AccountAccessModal
        isOpen={showAccountsModal}
        onClose={() => {
          setShowAccountsModal(false);
          setSelectedClient(null);
        }}
        client={selectedClient}
        addToast={addToast}
        onUpdate={fetchClients}
      />
      
      {/* Temporary Password Modal */}
      <Modal isOpen={!!tempPassword} onClose={() => setTempPassword(null)} title="Password Reset">
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">
            A new temporary password has been generated. Share this with the client:
          </p>
          
          <div className="bg-[#18181B] border border-white/10 rounded-xl p-4 flex items-center justify-between">
            <code className="text-lg font-mono text-[#22c55e]">{tempPassword}</code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(tempPassword);
                if (addToast) addToast('Password copied to clipboard', 'success');
              }}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-zinc-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
          
          <p className="text-xs text-zinc-500">
            The client should change this password after their first login.
          </p>
          
          <button
            onClick={() => setTempPassword(null)}
            className="w-full px-4 py-3 bg-[#6366f1] text-white rounded-xl font-bold hover:bg-[#5558e8] transition-colors"
          >
            Done
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default ClientPortal;
